import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { ROUTER_REQUEST } from '@ngrx/router-store';
import { Action, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { unionBy } from 'lodash-es';
import moment from 'moment';
import { combineLatest, forkJoin, interval, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  concatMap,
  filter,
  finalize,
  map,
  mapTo,
  mergeMap,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';

import * as Api from '@vi-iot/client';

import { retryFailedCall } from '@common';

import {
  DiagStackGenericResponse,
  DiagStackNodeError,
  FeatureNames,
  RemoteDiagnosticsVciAvailable,
} from '@vi-iot/client';
import { ViToastService } from '@vi-iot/ui';

import { RemoteDiagnosticsBackgroundService, UIService } from '@services';

import {
  AppInsightsMonitoringService,
  LogLevelEnum,
} from 'src/app/shared/services/app-insights/app-insights-monitoring.service';
import {
  APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP,
  APP_INSIGHTS_DIAGNOSTICS_OPERATIONS,
} from 'src/app/shared/services/app-insights/events/remote-diagnostics.events';
import { DiagnosticsConnectionForceDisconnectComponent } from '../../+installations/diagnostics-overlay/diagnostics-connection-box/diagnostics-connection-modal/diagnostics-connection-force-disconnect/diagnostics-connection-force-disconnect.component';
import { DiagnosticsChartService } from '../../+installations/diagnostics-overlay/tabs-content/extended-parameters/diagnostics-chart.service';
import { DiagnosticsUrlTypeEnum } from '../../+installations/installation/installation-overlay.model';
import { environment } from '../../../environments/environment';
import { VitoguideBackendSettingsApiPicker } from '../../../projects/client/apis/vitoguide-backend-settings/vitoguide-backend-settings.api-picker';
import { FeatureService } from '../_services';
import { AuthStore } from '../auth';
import { DiagStackDtcActions } from '../diag-stack-dtc';
import { DiagStackDtcService } from '../diag-stack-dtc/diag-stack-dtc.service';
import { InstallationDetailsStore } from '../installation-details';
import { RemoteDiagnosticsSnapshotsActions } from '../remote-diagnostics-snapshots/remote-diagnostics-snapshots.actions';
import { RemoteDiagnosticsUseCasesActions } from '../remote-diagnostics-use-cases';
import { BackendStorageActions } from '../settings';
import { VddDetailsListActions } from '../vdd-details-list/vdd-details-list.actions';

import { LogicalLinkHelpers } from '../_common/logical-link-helpers';
import { RemoteDiagnosticsActions } from './remote-diagnostics.actions';
import { RemoteDiagnosticsMappers } from './remote-diagnostics.mappers';
import {
  ActiveErrorWithDiagStackDetail,
  backendStorageSessionsKey,
  DeviceSerialMismatchError,
  DIAGNOSTICS_CONNECTION_STATUS,
  DIAGNOSTICS_CONNECTION_STEP,
  DiagStackBulkScheduleReadSchedulerOutcome,
  DiagStackBulkScheduleReadSchedulerOutcomeResolved,
  isUseCasePopupBlockedKey,
  localStorageSessionsKey,
  mainLogicalLink,
  RemoteDiagnosticsActiveSession,
  RemoteDiagnosticsParameter,
  RemoteDiagnosticsVddListParameter,
} from './remote-diagnostics.model';
import { RemoteDiagnosticsStore } from './remote-diagnostics.store';
import { OAuthService } from 'angular-oauth2-oidc';

@Injectable()
export class RemoteDiagnosticsEffects {
  getDiagStackInstanceAttempt$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsActions.diagstackGetFreeInstance.attempt),
      switchMap((action) =>
        this.remoteDiagnosticsCloudApiPicker
          .instance()
          .getDiagStackNodeNext()
          .pipe(
            retryFailedCall(3),
            tap(() => {
              this.appInsights.logEvent(
                APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.GET_FREE_DIAGSTACK_INSTANCE_SUCCESS,
                LogLevelEnum.TRACE
              );
            }),
            map((diagStackNode) =>
              RemoteDiagnosticsActions.diagstackGetFreeInstance.success({
                gatewaySerial: action.gatewaySerial,
                deviceSerial: action.deviceSerial,
                diagStackNode,
              })
            ),
            catchError((error: DiagStackNodeError) => {
              this.appInsights.logEvent(
                APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.GET_FREE_DIAGSTACK_INSTANCE_FAILED,
                LogLevelEnum.ERROR,
                { error }
              );

              return of(
                RemoteDiagnosticsActions.diagstackGetFreeInstance.failed({
                  gatewaySerial: action.gatewaySerial,
                  error,
                })
              );
            })
          )
      )
    );
  });

  getDiagStackInstanceSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsActions.diagstackGetFreeInstance.success),
      map((action) =>
        RemoteDiagnosticsActions.login.attempt({ gatewaySerial: action.gatewaySerial })
      )
    );
  });

  login$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsActions.login.attempt),
      mergeMap((action) =>
        combineLatest([
          of(action),
          of(this.oAuthService.getAccessToken()),
          this.authStore.state$.pipe(take(1)),
          this.remoteDiagnosticsStore.getDiagStackNode(action.gatewaySerial).pipe(take(1)),
        ])
      ),
      switchMap(([action, csrfToken, me, diagStackNode]) =>
        this.remoteDiagnosticsCloudApiPicker
          .instance()
          .userLogin(diagStackNode, csrfToken)
          .pipe(
            retryFailedCall(3),
            tap((session) => {
              if (!session.session) {
                throw new Error('Missing token');
              } else {
                this.appInsights.logEvent(
                  APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.LOGIN_SUCCESS,
                  LogLevelEnum.TRACE
                );
                this.appInsights.logEvent(
                  APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.GATEWAY_REMOTE_DIAGNOSTICS_OPEN_COMMAND_ATTEMPT,
                  LogLevelEnum.TRACE
                );
              }
            }),
            map((remoteDiagnosticsSession) =>
              RemoteDiagnosticsActions.login.success({
                gatewaySerial: action.gatewaySerial,
                remoteDiagnosticsSession,
                loginId: me.me.loginId,
              })
            ),
            catchError((error) => {
              this.appInsights.logEvent(
                APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.LOGIN_FAILED,
                LogLevelEnum.ERROR,
                { error }
              );

              return of(
                RemoteDiagnosticsActions.login.failed({ gatewaySerial: action.gatewaySerial })
              );
            })
          )
      )
    );
  });

  loginSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsActions.login.success),
      map((action) =>
        RemoteDiagnosticsActions.openSession.attempt({ gatewaySerial: action.gatewaySerial })
      )
    );
  });

  openSession$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsActions.openSession.attempt),
      mergeMap((action) =>
        combineLatest([of(action), this.remoteDiagnosticsStore.getConnectionReason().pipe(take(1))])
      ),
      switchMap(([action, reason]) =>
        this.remoteDiagnosticsCloudApiPicker
          .instance()
          .openDiagStackConnection(action.gatewaySerial, reason)
          .pipe(
            map((data) =>
              RemoteDiagnosticsActions.openSession.success({
                gatewaySerial: action.gatewaySerial,
                guid: data.data,
              })
            ),
            catchError((error) => {
              this.appInsights.logEvent(
                APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.OPEN_SESSION_FAILED,
                LogLevelEnum.ERROR,
                { error }
              );

              return of(
                RemoteDiagnosticsActions.openSession.failed({ gatewaySerial: action.gatewaySerial })
              );
            })
          )
      )
    );
  });

  storeActiveSessions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        RemoteDiagnosticsActions.parameter.add,
        RemoteDiagnosticsActions.parameter.delete,
        RemoteDiagnosticsActions.parameter.change,
        RemoteDiagnosticsActions.allParameters.select,
        RemoteDiagnosticsActions.allParameters.unselect,
        RemoteDiagnosticsActions.allParameters.changeInterval,
        RemoteDiagnosticsActions.loadVddList.success,
        RemoteDiagnosticsActions.logicalLinkOpen.success,
        RemoteDiagnosticsActions.deleteSelectedParameters,
        RemoteDiagnosticsActions.cancelParameterReadCycle.success,
        RemoteDiagnosticsActions.cancelMultipleParametersReadCycle.outcome,
        RemoteDiagnosticsActions.scheduleMultipleParametersReadCycle.outcome,
        RemoteDiagnosticsActions.scheduleParameterReadCycle.success,
        RemoteDiagnosticsActions.lastUserActivityTimestampReset,
        RemoteDiagnosticsActions.openLogicalLinkBeforeVddReading.success, // store opened LL for VDD read
        RemoteDiagnosticsUseCasesActions.extendedParametersTabChanged,
        RemoteDiagnosticsUseCasesActions.useCaseTypeChanged,
        RemoteDiagnosticsUseCasesActions.useCaseGroupChanged,
        RemoteDiagnosticsUseCasesActions.loadUseCaseVddListAttempt
      ),
      tap(() => {
        this.store$.dispatch(
          BackendStorageActions.GetKeyValueWithoutPropagation({ key: backendStorageSessionsKey })
        );
      }),
      switchMap((action) =>
        this.remoteDiagnosticsStore.loading$.pipe(
          filter((loading) => !loading),
          take(1),
          map(() => action)
        )
      ),
      concatLatestFrom(() => this.remoteDiagnosticsStore.getActiveSessions$),
      map(([action, activeSessions]) =>
        BackendStorageActions.SetKeyValue({
          key: backendStorageSessionsKey,
          value: { activeSessions },
          gatewaySerial: action.gatewaySerial,
        })
      )
    );
  });

  refreshActiveSessions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        RemoteDiagnosticsActions.closeSession.success,
        RemoteDiagnosticsActions.closeSession.aborted,
        RemoteDiagnosticsActions.mainLogicalLinkFromTopologyOpen.skipped,
        RemoteDiagnosticsActions.updateBusDetailsFromDtc.success
      ),
      concatLatestFrom(() => this.remoteDiagnosticsStore.getActiveSessions$),
      switchMap(([action, activeSessions]) =>
        this.settingsApiPicker
          .instance()
          .getKeyValueStorage(backendStorageSessionsKey)
          .pipe(
            retryFailedCall(3),
            tap((response) => {
              this.appInsights.logEvent(
                APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.GET_ACTIVE_SESSIONS_SUCCESS,
                LogLevelEnum.TRACE,
                { activeSessions: response?.activeSessions }
              );
            }),
            map((response) =>
              RemoteDiagnosticsActions.refreshActiveSessions.success({
                gatewaySerial: action.gatewaySerial,
                sessions: response?.activeSessions,
              })
            ),
            catchError((error) => {
              // for new account 'activeSessions' is not present in everest settings and we get 404
              if (error?.statusCode === 404) {
                return of(
                  RemoteDiagnosticsActions.refreshActiveSessions.empty({
                    gatewaySerial: action.gatewaySerial,
                    sessions: activeSessions,
                  })
                );
              }
            })
          )
      )
    );
  });

  refreshActiveSessionsSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        RemoteDiagnosticsActions.refreshActiveSessions.success,
        RemoteDiagnosticsActions.refreshActiveSessions.empty
      ),
      concatLatestFrom(() => [
        this.remoteDiagnosticsStore.getActiveSessions$,
        this.remoteDiagnosticsStore.getConnectionStatus(),
      ]),
      map(([action, sessions, connectionStatus]) => {
        let activeSessions = [];
        const localStorageKey = `${localStorageSessionsKey}${action.gatewaySerial}`;
        const localStorageItem = localStorage.getItem(localStorageKey);

        activeSessions = localStorageItem
          ? action.sessions?.filter((session) => session.gatewaySerial !== action.gatewaySerial)
          : unionBy(sessions, action.sessions, 'gatewaySerial');

        if (connectionStatus === DIAGNOSTICS_CONNECTION_STATUS.FAILED) {
          activeSessions = action.sessions;
        }

        // VIG-3755 - sometimes there's an issue when at 80% connection progress we set localStorage key and then
        // we remove it after a while. Additionally it's difficult to dynamically change sessions payload
        // Potential risk - mocked gateway serial is written in localStorage
        if (!localStorageItem) {
          localStorage.setItem(localStorageKey, 'true');
        } else {
          localStorage.removeItem(localStorageKey);
        }

        return BackendStorageActions.SetKeyValue({
          key: backendStorageSessionsKey,
          value: { activeSessions },
          gatewaySerial: action.gatewaySerial,
        });
      })
    );
  });

  setKeyValueSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BackendStorageActions.SetKeyValueSuccess),
      map((action) => {
        this.appInsights.logEvent(
          APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.UPDATE_ACTIVE_SESSIONS_SUCCESS,
          LogLevelEnum.TRACE,
          { activeSessions: action.value.activeSessions }
        );

        return RemoteDiagnosticsActions.updateActiveSessions({
          activeSessions: action.value.activeSessions,
        });
      })
    );
  });

  setKeyValueError$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BackendStorageActions.SetKeyValueError),
      map((action) =>
        RemoteDiagnosticsActions.disconnect.attempt({ gatewaySerial: action.gatewaySerial })
      )
    );
  });

  resetSequenceOnError$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        RemoteDiagnosticsActions.login.failed,
        RemoteDiagnosticsActions.openSession.failed,
        RemoteDiagnosticsActions.vciConnect.failed,
        RemoteDiagnosticsActions.remoteVciAvailabilityCheck.failed,
        RemoteDiagnosticsActions.remoteVciRegister.failed,
        RemoteDiagnosticsActions.mainLogicalLinkOpen.failed,
        RemoteDiagnosticsActions.logicalLinkOpen.failed,
        RemoteDiagnosticsActions.mainLogicalLinkClose.failed,
        RemoteDiagnosticsActions.mainLogicalLinkFromTopologyOpen.failed,
        RemoteDiagnosticsActions.diagstackGetFreeInstance.failed,
        DiagStackDtcActions.getActiveDtc.error,
        RemoteDiagnosticsActions.updateBusDetailsFromDtc.failed,
        RemoteDiagnosticsActions.fetchDeviceTopology.failed,
        RemoteDiagnosticsActions.fetchProductMatrix.failed,
        RemoteDiagnosticsActions.fetchProductMatrix.incorrectData
      ),
      concatMap((action) => [
        RemoteDiagnosticsActions.disconnect.attempt({ gatewaySerial: action.gatewaySerial }),
        RemoteDiagnosticsActions.setActiveConnectionStep({
          payload: DIAGNOSTICS_CONNECTION_STEP.CONNECTION_FAILED,
        }),
        RemoteDiagnosticsActions.setConnectionStatus({
          payload: DIAGNOSTICS_CONNECTION_STATUS.FAILED,
        }),
      ])
    );
  });

  loadActiveSessions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsActions.loadActiveSessions.attempt),
      switchMap(() =>
        of(
          BackendStorageActions.GetKeyValue({
            key: backendStorageSessionsKey,
          })
        )
      ),
      catchError((error) => {
        this.appInsights.logEvent(
          APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.GET_ACTIVE_SESSIONS_FAILED,
          LogLevelEnum.ERROR,
          { error }
        );

        return of(RemoteDiagnosticsActions.loadActiveSessions.failed());
      })
    );
  });

  loadKeyValueStorageSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BackendStorageActions.GetKeyValueSuccess),
      switchMap((action) => {
        const activeSessions: RemoteDiagnosticsActiveSession[] = action?.value?.activeSessions;
        if (!activeSessions?.length) {
          this.appInsights.logEvent(
            APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.GET_ACTIVE_SESSIONS_EMPTY,
            LogLevelEnum.TRACE
          );

          return of(RemoteDiagnosticsActions.loadActiveSessions.failed());
        }

        this.appInsights.logEvent(
          APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.GET_ACTIVE_SESSIONS_SUCCESS,
          LogLevelEnum.TRACE
        );

        return of(RemoteDiagnosticsActions.loadActiveSessions.success({ activeSessions }));
      })
    );
  });

  disconnectExpiredSessions$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(RemoteDiagnosticsActions.loadActiveSessions.success),
        map((action) => {
          const expiredSessions = action.activeSessions.filter((activeSession) => {
            const currentUTCDate = moment.utc().subtract(5, 'hours'); // current UTC date minus 5 hours
            const isOneDayExpired = currentUTCDate.isAfter(activeSession.timestamp);

            return isOneDayExpired;
          });

          if (expiredSessions.length > 0) {
            expiredSessions.forEach((expiredSession) => {
              this.remoteDiagnosticsStore.triggerDisconnectSequence(expiredSession.gatewaySerial);
            });
          }
        })
      );
    },
    { dispatch: false }
  );

  loadActiveSessionsSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(RemoteDiagnosticsActions.loadActiveSessions.success),
        concatLatestFrom(() => this.installationDetailsStore.gatewaySerial$),
        map(([_action, gatewaySerial]) => {
          this.remoteDiagnosticsBackgroundService.initLogicalLinkListInterval(gatewaySerial);
        })
      );
    },
    { dispatch: false }
  );

  loadVddParametersForControlUnit$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsActions.loadVddList.attempt),
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.remoteDiagnosticsStore
            .getDiagStackConnectionProperties(action.gatewaySerial)
            .pipe(take(1)),
        ])
      ),
      switchMap(([action, diagStackConnectionProperties]) =>
        this.remoteDiagnosticsCloudApiPicker
          .instance()
          .vddList(
            diagStackConnectionProperties.node,
            diagStackConnectionProperties.xAuthToken,
            action.logicalLinkName,
            diagStackConnectionProperties.vciName,
            diagStackConnectionProperties.vciResource
          )
          .pipe(
            concatMap((parameters) => {
              const availableParameters: RemoteDiagnosticsVddListParameter[] = this.remoteDiagnosticsMappers.vddListExtractor(
                parameters
              );

              return [
                RemoteDiagnosticsActions.loadVddList.success({
                  gatewaySerial: action.gatewaySerial,
                  parameters: availableParameters,
                }),
                RemoteDiagnosticsSnapshotsActions.loadVddListSuccess({
                  gatewaySerial: action.gatewaySerial,
                  parameters: availableParameters,
                }),
              ];
            }),
            catchError((error) => of(RemoteDiagnosticsActions.loadVddList.failed({ error })))
          )
      )
    );
  });

  remoteVciRegister$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsActions.remoteVciRegister.attempt),
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.remoteDiagnosticsStore
            .getXAuthTokenAndDiagStackNode(action.gatewaySerial)
            .pipe(take(1)),
        ])
      ),
      switchMap(([action, xAuthTokenAndDiagStackNode]) =>
        this.remoteDiagnosticsCloudApi
          .remoteVciRegister(
            xAuthTokenAndDiagStackNode.node,
            xAuthTokenAndDiagStackNode.xAuthToken,
            action.technicianWsUrl
          )
          .pipe(
            retryFailedCall(3),
            tap(() => {
              this.appInsights.logEvent(
                APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.REGISTER_REMOTE_VCI_SUCCESS,
                LogLevelEnum.TRACE
              );

              this.appInsights.logEvent(
                APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.REMOTE_VCI_AVAILABILITY_CHECK_ATTEMPT,
                LogLevelEnum.TRACE
              );
            }),
            concatMap(() => [
              RemoteDiagnosticsActions.remoteVciRegister.success({
                gatewaySerial: action.gatewaySerial,
                technicianWsUrl: action.technicianWsUrl,
              }),
              RemoteDiagnosticsActions.remoteVciAvailabilityCheck.attempt({
                gatewaySerial: action.gatewaySerial,
              }),
            ]),
            catchError((error) => {
              this.appInsights.logEvent(
                APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.REGISTER_REMOTE_VCI_FAILED,
                LogLevelEnum.ERROR,
                { error }
              );

              return of(
                RemoteDiagnosticsActions.remoteVciRegister.failed({
                  gatewaySerial: action.gatewaySerial,
                })
              );
            })
          )
      )
    );
  });

  vciAvailabilityCheck$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsActions.remoteVciAvailabilityCheck.attempt),
      tap((action) => this.createTimeout(action.gatewaySerial)),
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.remoteDiagnosticsStore
            .getXAuthTokenAndDiagStackNode(action.gatewaySerial)
            .pipe(take(1)),
          this.remoteDiagnosticsStore.getTechnicianWsUrl(action.gatewaySerial).pipe(take(1)),
        ])
      ),
      tap(() => (this.endPolling$ = new Subject())),
      switchMap((pollAction) =>
        interval(this.pollingIntervalMs).pipe(
          takeUntil(this.endPolling$),
          takeUntil(this.pollingUntil$),
          mapTo(pollAction),
          switchMap(([action, xAuthTokenAndDiagStackNode, technicianWsUrl]) =>
            this.remoteDiagnosticsCloudApiPicker
              .instance()
              .diagnosticServiceAvailableVci(
                xAuthTokenAndDiagStackNode.node,
                xAuthTokenAndDiagStackNode.xAuthToken
              )
              .pipe(
                map((response: RemoteDiagnosticsVciAvailable[]) => {
                  if (!Array.isArray(response) || !response.length) {
                    this.appInsights.logEvent(
                      APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.REMOTE_VCI_AVAILABILITY_CHECK_RESPONSE_EMPTY,
                      LogLevelEnum.TRACE,
                      { response }
                    );

                    return RemoteDiagnosticsActions.remoteVciAvailabilityCheck.responseEmpty();
                  }
                  let activeVci = response.find(
                    (res) =>
                      res?.additionalAttributes?.webSocketURL === technicianWsUrl ||
                      res?.additionalAttributes?.ipAddress === technicianWsUrl
                  );
                  if (!activeVci) {
                    this.appInsights.logEvent(
                      APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.REMOTE_VCI_AVAILABILITY_CHECK_RESPONSE_EMPTY,
                      LogLevelEnum.TRACE,
                      { response }
                    );

                    return RemoteDiagnosticsActions.remoteVciAvailabilityCheck.responseEmpty();
                  }
                  this.pollingUntil$.next(true);
                  clearTimeout(this.timer);
                  const preferredResource = 'ISO_13400_on_IEEE_802_3'; // this is the preferred resource for all devices
                  if (activeVci?.name && activeVci?.resources.includes(preferredResource)) {
                    this.appInsights.logEvent(
                      APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.REMOTE_VCI_AVAILABILITY_CHECK_SUCCESS,
                      LogLevelEnum.TRACE
                    );

                    return RemoteDiagnosticsActions.remoteVciAvailabilityCheck.success({
                      gatewaySerial: action.gatewaySerial,
                      remoteVciName: activeVci.name,
                      resource: preferredResource,
                      vinNumber: activeVci.additionalAttributes.vin,
                    });
                  } else {
                    this.appInsights.logEvent(
                      APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.REMOTE_VCI_AVAILABILITY_CHECK_FAILED,
                      LogLevelEnum.ERROR,
                      { response }
                    );

                    return RemoteDiagnosticsActions.remoteVciAvailabilityCheck.failed({
                      gatewaySerial: action.gatewaySerial,
                    });
                  }
                }),
                catchError((error) => {
                  this.appInsights.logEvent(
                    APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.REMOTE_VCI_AVAILABILITY_CHECK_FAILED,
                    LogLevelEnum.ERROR,
                    { error }
                  );

                  return of(
                    RemoteDiagnosticsActions.remoteVciAvailabilityCheck.failed({
                      gatewaySerial: action.gatewaySerial,
                    })
                  );
                })
              )
          )
        )
      )
    );
  });

  vciConnectionTrigger$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsActions.remoteVciAvailabilityCheck.success),
      map((action) =>
        RemoteDiagnosticsActions.vciConnect.attempt({ gatewaySerial: action.gatewaySerial })
      )
    );
  });

  fetchSetupInfo$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsActions.setupInfo.attempt),
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.remoteDiagnosticsStore
            .getXAuthTokenAndDiagStackNode(action.gatewaySerial)
            .pipe(take(1)),
        ])
      ),
      switchMap(([action, xAuthTokenAndDiagStackNode]) =>
        this.remoteDiagnosticsCloudApiPicker
          .instance()
          .setupInfo(xAuthTokenAndDiagStackNode.node, xAuthTokenAndDiagStackNode.xAuthToken)
          .pipe(
            map((setupInfo) =>
              RemoteDiagnosticsActions.setupInfo.success({
                gatewaySerial: action.gatewaySerial,
                setupInfo,
              })
            ),
            catchError((error) => {
              this.appInsights.logEvent(
                APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.SETUP_INFO_FAILED,
                LogLevelEnum.ERROR,
                { error }
              );

              return of(RemoteDiagnosticsActions.setupInfo.failed());
            })
          )
      )
    );
  });

  vciConnect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsActions.vciConnect.attempt),
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.remoteDiagnosticsStore
            .getDiagStackConnectionProperties(action.gatewaySerial)
            .pipe(take(1)),
        ])
      ),
      switchMap(([action, diagStackConnectionProperties]) =>
        this.remoteDiagnosticsCloudApi
          .diagnosticServiceConnectVci(
            diagStackConnectionProperties.node,
            diagStackConnectionProperties.xAuthToken,
            diagStackConnectionProperties.vciName,
            diagStackConnectionProperties.vciResource
          )
          .pipe(
            retryFailedCall(3),
            tap(() => {
              this.appInsights.logEvent(
                APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.REMOTE_VCI_CONNECT_SUCCESS,
                LogLevelEnum.TRACE
              );
            }),
            concatMap(() => [
              RemoteDiagnosticsActions.vciConnect.success(),
              RemoteDiagnosticsActions.setupInfo.attempt({
                gatewaySerial: action.gatewaySerial,
              }),
              RemoteDiagnosticsActions.mainLogicalLinkOpen.attempt({
                gatewaySerial: action.gatewaySerial,
              }),
            ]),
            catchError((error) => {
              this.appInsights.logEvent(
                APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.REMOTE_VCI_CONNECT_FAILED,
                LogLevelEnum.ERROR,
                { error }
              );

              return of(
                RemoteDiagnosticsActions.vciConnect.failed({ gatewaySerial: action.gatewaySerial })
              );
            })
          )
      )
    );
  });

  // this is main logical link - should work with all viessmann devices
  openMainLogicalLink$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsActions.mainLogicalLinkOpen.attempt),
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.remoteDiagnosticsStore
            .getDiagStackConnectionProperties(action.gatewaySerial)
            .pipe(take(1)),
          this.remoteDiagnosticsStore.getDiagStackSetupInfo(action.gatewaySerial).pipe(take(1)),
        ])
      ),
      switchMap(([action, diagStackConnectionProperties, setupInfo]) =>
        this.remoteDiagnosticsCloudApi
          .diagnosticServiceLogicalLinkOpen(
            diagStackConnectionProperties.node,
            diagStackConnectionProperties.xAuthToken,
            mainLogicalLink,
            diagStackConnectionProperties.vciName,
            diagStackConnectionProperties.vciResource,
            true,
            APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.MAIN_LOGICAL_LINK_OPEN_ATTEMPT
          )
          .pipe(
            retryFailedCall(3),
            tap(() => {
              this.appInsights.logEvent(
                APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.MAIN_LOGICAL_LINK_OPEN_SUCCESS,
                LogLevelEnum.TRACE
              );
            }),
            concatMap(() => [
              RemoteDiagnosticsActions.mainLogicalLinkOpen.success({
                gatewaySerial: action.gatewaySerial,
                logicalLinkName: mainLogicalLink,
              }),
              RemoteDiagnosticsActions.fetchProductMatrix.attempt({
                gatewaySerial: action.gatewaySerial,
              }),
            ]),
            catchError((error) => {
              this.appInsights.logEvent(
                APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.MAIN_LOGICAL_LINK_OPEN_FAILED,
                LogLevelEnum.ERROR,
                { error, setupInfo }
              );

              return of(
                RemoteDiagnosticsActions.mainLogicalLinkOpen.failed({
                  gatewaySerial: action.gatewaySerial,
                })
              );
            })
          )
      )
    );
  });

  fetchProductMatrix$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsActions.fetchProductMatrix.attempt),
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.remoteDiagnosticsStore
            .getDiagStackConnectionProperties(action.gatewaySerial)
            .pipe(take(1)),
          this.remoteDiagnosticsStore.getDiagStackSetupInfo(action.gatewaySerial).pipe(take(1)),
        ])
      ),
      switchMap(([action, diagStackConnectionProperties, setupInfo]) =>
        this.remoteDiagnosticsCloudApiPicker
          .instance()
          .diagnosticProductMatrix(
            diagStackConnectionProperties.node,
            diagStackConnectionProperties.xAuthToken,
            mainLogicalLink,
            diagStackConnectionProperties.vciResource,
            diagStackConnectionProperties.vciName
          )
          .pipe(
            tap(() => {
              this.appInsights.logEvent(
                APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.FETCH_PRODUCT_MATRIX_SUCCESS,
                LogLevelEnum.TRACE
              );
            }),
            map((productMatrix) =>
              this.remoteDiagnosticsMappers.productMatrixMapper(productMatrix)
            ),
            map((currentProductMatrix) =>
              RemoteDiagnosticsActions.fetchProductMatrix.success({
                gatewaySerial: action.gatewaySerial,
                filteredProductMatrix: currentProductMatrix,
              })
            ),
            catchError((error) => {
              this.appInsights.logEvent(
                APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.FETCH_PRODUCT_MATRIX_FAILED,
                LogLevelEnum.ERROR,
                { error, setupInfo }
              );

              return of(
                RemoteDiagnosticsActions.fetchProductMatrix.failed({
                  gatewaySerial: action.gatewaySerial,
                })
              );
            })
          )
      )
    );
  });

  fetchProductMatrixSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsActions.fetchProductMatrix.success),
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.remoteDiagnosticsStore.getConnectedDeviceSerial(action.gatewaySerial).pipe(take(1)),
          this.remoteDiagnosticsStore.getDiagStackSetupInfo(action.gatewaySerial).pipe(take(1)),
        ])
      ),
      tap(([action, deviceSerial]) => {
        if (action.filteredProductMatrix.length > 1) {
          const isAnyDeviceSerialPresent = action.filteredProductMatrix.some(
            (matrix) => matrix.viessmannIdentificationNumber === deviceSerial
          );

          if (!isAnyDeviceSerialPresent) {
            const error = new DeviceSerialMismatchError(
              `Device serial mismatch, product matrix response ` +
                JSON.stringify(action.filteredProductMatrix) +
                ` serial we were looking for - deviceSerialFeature: ${deviceSerial}`
            );

            if (environment.env !== 'production') {
              console.error(error.message);
            }

            return RemoteDiagnosticsActions.fetchProductMatrix.incorrectData({
              gatewaySerial: action.gatewaySerial,
            });
          }
        }
      }),
      map(([action, deviceSerial, setupInfo]) => {
        let foundMainLogicalLink = null;
        if (action.filteredProductMatrix.length > 1) {
          foundMainLogicalLink = action.filteredProductMatrix.find(
            (matrix) => matrix.viessmannIdentificationNumber === deviceSerial
          )?.logicalLinkName;
        } else {
          foundMainLogicalLink = action.filteredProductMatrix[0].logicalLinkName;
        }

        if (foundMainLogicalLink === '') {
          foundMainLogicalLink = mainLogicalLink; // it worked for fetching product matrix so we can try with topology
        }

        if (foundMainLogicalLink === null) {
          this.appInsights.logEvent(
            APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.FETCH_PRODUCT_MATRIX_FAILED,
            LogLevelEnum.ERROR,
            { setupInfo }
          );

          return RemoteDiagnosticsActions.fetchProductMatrix.incorrectData({
            gatewaySerial: action.gatewaySerial,
          });
        }

        // already open to fetch product matrix
        if (mainLogicalLink === foundMainLogicalLink) {
          return RemoteDiagnosticsActions.fetchDeviceTopology.attempt({
            gatewaySerial: action.gatewaySerial,
            mainLogicalLinkFromProductMatrix: mainLogicalLink,
          });
        } else {
          return RemoteDiagnosticsActions.openDeviceMainLogicalLink.attempt({
            gatewaySerial: action.gatewaySerial,
            mainLogicalLinkFromProductMatrix: foundMainLogicalLink,
          });
        }
      })
    );
  });

  // this is main logical link for the device - taken from product matrix
  openDeviceMainLogicalLink$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsActions.openDeviceMainLogicalLink.attempt),
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.remoteDiagnosticsStore
            .getDiagStackConnectionProperties(action.gatewaySerial)
            .pipe(take(1)),
          this.remoteDiagnosticsStore.getDiagStackSetupInfo(action.gatewaySerial).pipe(take(1)),
        ])
      ),
      switchMap(([action, diagStackConnectionProperties, setupInfo]) =>
        this.remoteDiagnosticsCloudApi
          .diagnosticServiceLogicalLinkOpen(
            diagStackConnectionProperties.node,
            diagStackConnectionProperties.xAuthToken,
            action.mainLogicalLinkFromProductMatrix,
            diagStackConnectionProperties.vciName,
            diagStackConnectionProperties.vciResource,
            true,
            APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.OPEN_DEVICE_MAIN_LOGICAL_LINK_ATTEMPT
          )
          .pipe(
            tap(() => {
              this.appInsights.logEvent(
                APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.OPEN_DEVICE_MAIN_LOGICAL_LINK_SUCCESS,
                LogLevelEnum.TRACE
              );
            }),
            concatMap(() => [
              RemoteDiagnosticsActions.openDeviceMainLogicalLink.success({
                mainLogicalLinkFromProductMatrix: action.mainLogicalLinkFromProductMatrix,
              }),
              RemoteDiagnosticsActions.fetchDeviceTopology.attempt({
                gatewaySerial: action.gatewaySerial,
                mainLogicalLinkFromProductMatrix: action.mainLogicalLinkFromProductMatrix,
              }),
            ]),
            catchError((error) => {
              this.appInsights.logEvent(
                APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.OPEN_DEVICE_MAIN_LOGICAL_LINK_FAILED,
                LogLevelEnum.ERROR,
                { error, setupInfo }
              );

              return of(RemoteDiagnosticsActions.openDeviceMainLogicalLink.failed());
            })
          )
      )
    );
  });
  // open main logical link from topology and store it for usage later ( for vdd's which we dont know their LL )
  fetchDeviceTopology$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsActions.fetchDeviceTopology.attempt),
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.remoteDiagnosticsStore
            .getDiagStackConnectionProperties(action.gatewaySerial)
            .pipe(take(1)),
          this.remoteDiagnosticsStore.getDiagStackSetupInfo(action.gatewaySerial).pipe(take(1)),
        ])
      ),
      switchMap(([action, diagStackConnectionProperties, setupInfo]) =>
        this.remoteDiagnosticsCloudApiPicker
          .instance()
          .scanTopology(
            diagStackConnectionProperties.node,
            diagStackConnectionProperties.xAuthToken,
            action.mainLogicalLinkFromProductMatrix,
            diagStackConnectionProperties.vciResource,
            diagStackConnectionProperties.vciName
          )
          .pipe(
            retryFailedCall(3),
            tap(() => {
              this.appInsights.logEvent(
                APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.FETCH_TOPOLOGY_SUCCESS,
                LogLevelEnum.TRACE
              );
            }),
            concatMap((topology) => [
              RemoteDiagnosticsActions.fetchDeviceTopology.success(),
              RemoteDiagnosticsActions.loadControlUnits.success({
                gatewaySerial: action.gatewaySerial,
                controlUnits: this.remoteDiagnosticsMappers.topologyControlUnitsExtractor(topology),
              }),
              RemoteDiagnosticsActions.saveBusDetailsFromTopology.success({
                gatewaySerial: action.gatewaySerial,
                busDetailsFromTopology: this.remoteDiagnosticsMappers.topologyBusDetailsExtractor(
                  topology
                ),
              }),
              RemoteDiagnosticsActions.saveMainLogicalLinkFromTopology({
                gatewaySerial: action.gatewaySerial,
                logicalLinkName: this.remoteDiagnosticsMappers.topologyMainEcuExtractor(topology),
              }),

              RemoteDiagnosticsActions.saveBundleIdFromTopology({
                gatewaySerial: action.gatewaySerial,
                bundleId: this.remoteDiagnosticsMappers.extractBundleId(topology),
              }),

              RemoteDiagnosticsActions.mainLogicalLinkClose.attempt({
                gatewaySerial: action.gatewaySerial,
              }),
            ]),
            catchError((error) => {
              this.appInsights.logEvent(
                APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.FETCH_TOPOLOGY_FAILED,
                LogLevelEnum.ERROR,
                { error, setupInfo }
              );

              return of(
                RemoteDiagnosticsActions.fetchDeviceTopology.failed({
                  gatewaySerial: action.gatewaySerial,
                })
              );
            })
          )
      )
    );
  });

  closeMainLogicalLinkAfterFetchingTopology = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsActions.mainLogicalLinkClose.attempt),
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.remoteDiagnosticsStore
            .getDiagStackConnectionProperties(action.gatewaySerial)
            .pipe(take(1)),
          this.remoteDiagnosticsStore.getDiagStackSetupInfo(action.gatewaySerial).pipe(take(1)),
        ])
      ),
      switchMap(([action, diagStackConnectionProperties, setupInfo]) =>
        this.remoteDiagnosticsCloudApi
          .diagnosticServiceLogicalLinkClose(
            diagStackConnectionProperties.node,
            diagStackConnectionProperties.xAuthToken,
            mainLogicalLink,
            diagStackConnectionProperties.vciName,
            diagStackConnectionProperties.vciResource
          )
          .pipe(
            retryFailedCall(3),
            tap(() => {
              this.appInsights.logEvent(
                APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.MAIN_LOGICAL_LINK_CLOSE_SUCCESS,
                LogLevelEnum.TRACE
              );
            }),
            map(() =>
              RemoteDiagnosticsActions.mainLogicalLinkClose.success({
                gatewaySerial: action.gatewaySerial,
                logicalLinkName: mainLogicalLink,
              })
            ),
            catchError((error) => {
              this.appInsights.logEvent(
                APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.MAIN_LOGICAL_LINK_CLOSE_FAILED,
                LogLevelEnum.ERROR,
                { error, setupInfo }
              );

              return of(
                RemoteDiagnosticsActions.mainLogicalLinkClose.failed({
                  gatewaySerial: action.gatewaySerial,
                })
              );
            })
          )
      )
    );
  });

  closeMainLogicalLinkAfterFetchingTopologySuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsActions.mainLogicalLinkClose.success),
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.remoteDiagnosticsStore
            .getMainLogicalLinkFromTopology(action.gatewaySerial)
            .pipe(take(1)),
        ])
      ),
      map(([action, mainLogicalLinkFromTopology]) => {
        if (!mainLogicalLinkFromTopology) {
          // it means there is no product definition found in ODX for read VIN.
          return RemoteDiagnosticsActions.mainLogicalLinkFromTopologyOpen.skipped({
            gatewaySerial: action.gatewaySerial,
          });
        }

        return RemoteDiagnosticsActions.mainLogicalLinkFromTopologyOpen.attempt({
          gatewaySerial: action.gatewaySerial,
        });
      })
    );
  });

  openMainLogicalLinkFromTopology$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsActions.mainLogicalLinkFromTopologyOpen.attempt),
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.remoteDiagnosticsStore
            .getDiagStackConnectionProperties(action.gatewaySerial)
            .pipe(take(1)),
          this.remoteDiagnosticsStore
            .getMainLogicalLinkFromTopology(action.gatewaySerial)
            .pipe(take(1)),
          this.remoteDiagnosticsStore.getDiagStackSetupInfo(action.gatewaySerial).pipe(take(1)),
        ])
      ),
      switchMap(([action, diagStackConnectionProperties, mainLogicalLinkFromTopology, setupInfo]) =>
        this.remoteDiagnosticsCloudApi
          .diagnosticServiceLogicalLinkOpen(
            diagStackConnectionProperties.node,
            diagStackConnectionProperties.xAuthToken,
            mainLogicalLinkFromTopology,
            diagStackConnectionProperties.vciName,
            diagStackConnectionProperties.vciResource,
            true,
            APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.MAIN_LOGICAL_LINK_FROM_TOPOLOGY_OPEN_ATTEMPT
          )
          .pipe(
            retryFailedCall(3),
            tap(() => {
              this.appInsights.logEvent(
                APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.MAIN_LOGICAL_LINK_FROM_TOPOLOGY_OPEN_SUCCESS,
                LogLevelEnum.TRACE
              );
            }),
            map(() =>
              RemoteDiagnosticsActions.mainLogicalLinkFromTopologyOpen.success({
                gatewaySerial: action.gatewaySerial,
                logicalLinkName: mainLogicalLinkFromTopology,
              })
            ),
            catchError((error) => {
              this.appInsights.logEvent(
                APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.MAIN_LOGICAL_LINK_FROM_TOPOLOGY_OPEN_FAILED,
                LogLevelEnum.ERROR,
                { error, setupInfo }
              );

              return of(
                RemoteDiagnosticsActions.mainLogicalLinkFromTopologyOpen.failed({
                  gatewaySerial: action.gatewaySerial,
                })
              );
            })
          )
      )
    );
  });

  fetchActiveDtc$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsActions.mainLogicalLinkFromTopologyOpen.success),
      map((action) =>
        DiagStackDtcActions.getActiveDtc.attempt({ gatewaySerial: action.gatewaySerial })
      )
    );
  });

  getActiveDtcSuccessTopologyMerge$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DiagStackDtcActions.getActiveDtc.success),
      concatLatestFrom((action) =>
        this.remoteDiagnosticsStore.getBusDetailsFromTopology(action.gatewaySerial)
      ),
      switchMap(([action, busDetailsFromTopologyFromStore]) =>
        of(
          this.mergeTopologyBusDetailsWithDtcErrors(
            busDetailsFromTopologyFromStore,
            action.activeDtcResponse
          )
        ).pipe(
          map(
            (busDetailsFromTopology) =>
              RemoteDiagnosticsActions.updateBusDetailsFromDtc.success({
                busDetailsFromTopology,
                gatewaySerial: action.gatewaySerial,
              }),
            catchError(() =>
              of(
                RemoteDiagnosticsActions.updateBusDetailsFromDtc.failed({
                  gatewaySerial: action.gatewaySerial,
                })
              )
            )
          )
        )
      )
    );
  });

  openLogicalLink$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsActions.logicalLinkOpen.attempt),
      tap(() => this.uiService.blockUI()),
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.remoteDiagnosticsStore
            .getDiagStackConnectionProperties(action.gatewaySerial)
            .pipe(take(1)),
          this.remoteDiagnosticsStore.getActiveLogicalLinks(action.gatewaySerial).pipe(take(1)),
          this.remoteDiagnosticsStore
            .getMainLogicalLinkFromTopology(action.gatewaySerial)
            .pipe(take(1)),

          this.remoteDiagnosticsStore.getDiagStackSetupInfo(action.gatewaySerial).pipe(take(1)),
        ])
      ),
      switchMap(
        ([
          action,
          diagStackConnectionProperties,
          activeLogicalLinkList,
          deviceMainLogicalLinkFromTopology,
          setupInfo,
        ]) => {
          if (activeLogicalLinkList.includes(action.logicalLinkName)) {
            this.uiService.unblockUI();

            return of(
              RemoteDiagnosticsActions.logicalLinkOpen.abort({
                gatewaySerial: action.gatewaySerial,
                logicalLinkName: action.logicalLinkName,
              })
            );
          } else {
            if (LogicalLinkHelpers.shouldLogicalLinkBeForwardOpen(action.logicalLinkName)) {
              return this.remoteDiagnosticsCloudApiPicker
                .instance()
                .diagnosticServiceForwardLogicalLinkOpen(
                  diagStackConnectionProperties.node,
                  diagStackConnectionProperties.xAuthToken,
                  action.logicalLinkName,
                  deviceMainLogicalLinkFromTopology,
                  diagStackConnectionProperties.vciName,
                  diagStackConnectionProperties.vciResource,
                  true
                )
                .pipe(
                  tap(() => this.uiService.unblockUI()),
                  map(() =>
                    RemoteDiagnosticsActions.logicalLinkOpen.success({
                      gatewaySerial: action.gatewaySerial,
                      logicalLinkName: action.logicalLinkName,
                    })
                  ),
                  catchError((error) => {
                    this.uiService.unblockUI();

                    this.appInsights.logEvent(
                      APP_INSIGHTS_DIAGNOSTICS_OPERATIONS.OPEN_LOGICAL_LINK_FAILED,
                      LogLevelEnum.ERROR,
                      { error, setupInfo }
                    );

                    return of(
                      RemoteDiagnosticsActions.logicalLinkOpen.failed({
                        gatewaySerial: action.gatewaySerial,
                        error,
                      })
                    );
                  })
                );
            } else {
              return this.remoteDiagnosticsCloudApi
                .diagnosticServiceLogicalLinkOpen(
                  diagStackConnectionProperties.node,
                  diagStackConnectionProperties.xAuthToken,
                  action.logicalLinkName,
                  diagStackConnectionProperties.vciName,
                  diagStackConnectionProperties.vciResource,
                  true
                )
                .pipe(
                  tap(() => this.uiService.unblockUI()),
                  map(() =>
                    RemoteDiagnosticsActions.logicalLinkOpen.success({
                      gatewaySerial: action.gatewaySerial,
                      logicalLinkName: action.logicalLinkName,
                    })
                  ),
                  catchError((error) => {
                    this.uiService.unblockUI();

                    this.appInsights.logEvent(
                      APP_INSIGHTS_DIAGNOSTICS_OPERATIONS.OPEN_LOGICAL_LINK_FAILED,
                      LogLevelEnum.ERROR,
                      { error, setupInfo }
                    );

                    return of(
                      RemoteDiagnosticsActions.logicalLinkOpen.failed({
                        gatewaySerial: action.gatewaySerial,
                        error,
                      })
                    );
                  })
                );
            }
          }
        }
      )
    );
  });

  loadVddListAfterLogicalLinkIsOpened$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        RemoteDiagnosticsActions.logicalLinkOpen.abort,
        RemoteDiagnosticsActions.logicalLinkOpen.success
      ),
      map((action) =>
        RemoteDiagnosticsActions.loadVddList.attempt({
          gatewaySerial: action.gatewaySerial,
          logicalLinkName: action.logicalLinkName,
        })
      )
    );
  });

  openLogicalLinkBeforeVddReading$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsActions.openLogicalLinkBeforeVddReading.attempt),
      tap(() => this.uiService.blockUI()),
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.remoteDiagnosticsStore
            .getDiagStackConnectionProperties(action.gatewaySerial)
            .pipe(take(1)),
          this.remoteDiagnosticsStore.getActiveLogicalLinks(action.gatewaySerial).pipe(take(1)),
          this.remoteDiagnosticsStore.getDiagStackSetupInfo(action.gatewaySerial).pipe(take(1)),
        ])
      ),
      mergeMap(([action, diagStackConnectionProperties, activeLogicalLinkList, setupInfo]) => {
        if (activeLogicalLinkList.includes(action.logicalLinkName)) {
          this.uiService.unblockUI();

          return of(
            RemoteDiagnosticsActions.openLogicalLinkBeforeVddReading.alreadyOpen({
              gatewaySerial: action.gatewaySerial,
              logicalLinkName: action.logicalLinkName,
              vddNames: action.vddNames,
            })
          );
        } else {
          return this.remoteDiagnosticsCloudApi
            .diagnosticServiceLogicalLinkOpen(
              diagStackConnectionProperties.node,
              diagStackConnectionProperties.xAuthToken,
              action.logicalLinkName,
              diagStackConnectionProperties.vciName,
              diagStackConnectionProperties.vciResource,
              true
            )
            .pipe(
              tap(() => this.uiService.unblockUI()),
              map(() =>
                RemoteDiagnosticsActions.openLogicalLinkBeforeVddReading.success({
                  gatewaySerial: action.gatewaySerial,
                  logicalLinkName: action.logicalLinkName,
                  vddNames: action.vddNames,
                })
              ),
              catchError((error) => {
                this.uiService.unblockUI();

                this.appInsights.logEvent(
                  APP_INSIGHTS_DIAGNOSTICS_OPERATIONS.OPEN_LOGICAL_LINK_FAILED,
                  LogLevelEnum.ERROR,
                  { error, setupInfo }
                );

                return of(
                  RemoteDiagnosticsActions.openLogicalLinkBeforeVddReading.failed({
                    gatewaySerial: action.gatewaySerial,
                  })
                );
              })
            );
        }
      })
    );
  });

  scheduleVddReadAfterLogicalLinkIsOpened$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        RemoteDiagnosticsActions.openLogicalLinkBeforeVddReading.alreadyOpen,
        RemoteDiagnosticsActions.openLogicalLinkBeforeVddReading.success
      ),
      concatMap((action) =>
        action.vddNames.map((vddName) =>
          VddDetailsListActions.GetStructureAndValueForVddListAttempt({
            vddNames: [vddName],
            gatewaySerial: action.gatewaySerial,
            logicalLinkName: action.logicalLinkName,
          })
        )
      )
    );
  });

  disconnect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsActions.disconnect.attempt),
      tap(() => this.uiService.blockUI()),
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.remoteDiagnosticsStore
            .getXAuthTokenAndDiagStackNode(action.gatewaySerial)
            .pipe(take(1)),
          this.remoteDiagnosticsStore.getDiagStackSetupInfo(action.gatewaySerial).pipe(take(1)),
        ])
      ),
      switchMap(([action, xAuthTokenAndDiagStackNode, setupInfo]) =>
        this.remoteDiagnosticsCloudApi
          .userLogout(xAuthTokenAndDiagStackNode.node, xAuthTokenAndDiagStackNode.xAuthToken)
          .pipe(
            tap(() => {
              this.uiService.unblockUI();
              this.viToastService.warning({
                message: this.translateService.instant(
                  'DIAGNOSTICS.CONNECTION_MODAL.DISCONNECT_SNACK_BAR_TEXT'
                ),
              });
            }),
            map(() =>
              RemoteDiagnosticsActions.disconnect.success({
                gatewaySerial: action.gatewaySerial,
              })
            ),
            catchError((error) => {
              this.uiService.unblockUI();

              this.appInsights.logEvent(
                APP_INSIGHTS_DIAGNOSTICS_OPERATIONS.DISCONNECT_FAILED,
                LogLevelEnum.ERROR,
                { error, setupInfo }
              );

              return of(
                RemoteDiagnosticsActions.disconnect.failed({
                  gatewaySerial: action.gatewaySerial,
                  error,
                })
              );
            })
          )
      )
    );
  });

  disconnectFailed$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsActions.disconnect.failed),
      concatLatestFrom(() => this.remoteDiagnosticsStore.getActiveSessions$),
      map(([action, activeSessions]) =>
        BackendStorageActions.SetKeyValue({
          key: backendStorageSessionsKey,
          value: {
            activeSessions: activeSessions.filter(
              (session) => session.gatewaySerial !== action.gatewaySerial
            ),
          },
          gatewaySerial: action.gatewaySerial,
        })
      )
    );
  });

  disconnectSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsActions.disconnect.success),
      concatLatestFrom((action) =>
        this.remoteDiagnosticsStore.getSessionGuid(action.gatewaySerial)
      ),
      map(([action, sessionGuid]) => {
        if (sessionGuid) {
          return RemoteDiagnosticsActions.closeSession.attempt({
            gatewaySerial: action.gatewaySerial,
          });
        } else {
          return RemoteDiagnosticsActions.closeSession.aborted({
            gatewaySerial: action.gatewaySerial,
          });
        }
      })
    );
  });

  closeSession$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsActions.closeSession.attempt),
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.remoteDiagnosticsStore.getSessionGuid(action.gatewaySerial).pipe(take(1)),
        ])
      ),
      switchMap(([action, guid]) =>
        this.remoteDiagnosticsCloudApiPicker
          .instance()
          .closeDiagStackConnection(action.gatewaySerial, guid)
          .pipe(
            map(() =>
              RemoteDiagnosticsActions.closeSession.success({
                gatewaySerial: action.gatewaySerial,
              })
            ),
            catchError((error) => {
              this.appInsights.logEvent(
                APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.CLOSE_EXISTING_SESSION_FAILED,
                LogLevelEnum.ERROR,
                { error }
              );

              return of(
                RemoteDiagnosticsActions.closeSession.failed({
                  gatewaySerial: action.gatewaySerial,
                })
              );
            }),
            finalize(() => {
              // After logout and close gateway.remoteDiagnostics feature
              // We have to fetch updated feature state in order to display the corresponding view for the user
              setTimeout(() => {
                this.featureService.fetchGatewayFeature(FeatureNames.gatewayRemoteDiagnostics);
              }, 2000);
            })
          )
      )
    );
  });

  scheduleCyclicParameterRead$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsActions.scheduleParameterReadCycle.attempt),
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.remoteDiagnosticsStore
            .getDiagStackConnectionProperties(action.gatewaySerial)
            .pipe(take(1)),
        ])
      ),
      switchMap(([action, diagStackConnectionProperties]) =>
        this.remoteDiagnosticsCloudApiPicker
          .instance()
          .diagnosticServiceDataRead(
            diagStackConnectionProperties.node,
            diagStackConnectionProperties.xAuthToken,
            action.parameter.controlUnit.value,
            diagStackConnectionProperties.vciResource,
            diagStackConnectionProperties.vciName,
            action.parameter.vddDetail.name,
            action.parameter.interval
          )
          .pipe(
            map((requestId) =>
              RemoteDiagnosticsActions.scheduleParameterReadCycle.success({
                parameterId: action.parameter.id,
                requestId,
                gatewaySerial: action.gatewaySerial,
              })
            ),
            catchError(() => of(RemoteDiagnosticsActions.scheduleParameterReadCycle.failed()))
          )
      )
    );
  });

  cancelCyclicParameterRead$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsActions.cancelParameterReadCycle.attempt),
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.remoteDiagnosticsStore
            .getXAuthTokenAndDiagStackNode(action.gatewaySerial)
            .pipe(take(1)),
        ])
      ),
      mergeMap(([action, xAuthTokenAndDiagStackNode]) =>
        this.remoteDiagnosticsCloudApiPicker
          .instance()
          .diagnosticServiceCancelCycle(
            xAuthTokenAndDiagStackNode.node,
            xAuthTokenAndDiagStackNode.xAuthToken,
            action.parameter.requestId
          )
          .pipe(
            map(() =>
              RemoteDiagnosticsActions.cancelParameterReadCycle.success({
                parameterId: action.parameter.id,
                gatewaySerial: action.gatewaySerial,
              })
            ),
            catchError(() => of(RemoteDiagnosticsActions.scheduleParameterReadCycle.failed()))
          )
      )
    );
  });

  cancelMultipleCyclicParametersRead$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsActions.cancelMultipleParametersReadCycle.attempt),
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.remoteDiagnosticsStore
            .getXAuthTokenAndDiagStackNode(action.gatewaySerial)
            .pipe(take(1)),
          action.onlySelected === true
            ? this.remoteDiagnosticsStore
                .getParametersStartedAndSelected(action.gatewaySerial)
                .pipe(take(1))
            : this.remoteDiagnosticsStore
                .getParametersWhichAreBeingRead(action.gatewaySerial)
                .pipe(take(1)),
        ])
      ),
      mergeMap(([action, xAuthTokenAndDiagStackNode, parameters]) => {
        if (parameters.length === 0) {
          return of(
            RemoteDiagnosticsActions.cancelMultipleParametersReadCycle.aborted({
              gatewaySerial: action.gatewaySerial,
            })
          );
        }

        let cancelRunningCycles: DiagStackBulkScheduleReadSchedulerOutcome = {};
        parameters.forEach((parameter: RemoteDiagnosticsParameter) => {
          cancelRunningCycles[parameter.id] = this.remoteDiagnosticsCloudApiPicker
            .instance()
            .diagnosticServiceCancelCycle(
              xAuthTokenAndDiagStackNode.node,
              xAuthTokenAndDiagStackNode.xAuthToken,
              parameter.requestId
            )
            .pipe(
              map((response) => response),
              catchError(() => of(null))
            );
        });

        return forkJoin(cancelRunningCycles).pipe(
          map((stopCyclesOutcome: DiagStackBulkScheduleReadSchedulerOutcomeResolved) => {
            const isCancelCyclesFailed = Object.values(stopCyclesOutcome).every(
              (subscriptionResult) => subscriptionResult === null
            );

            if (isCancelCyclesFailed) {
              return RemoteDiagnosticsActions.cancelMultipleParametersReadCycle.failed();
            } else {
              return RemoteDiagnosticsActions.cancelMultipleParametersReadCycle.outcome({
                gatewaySerial: action.gatewaySerial,
                transactionOutcome: stopCyclesOutcome,
              });
            }
          })
        );
      })
    );
  });

  cancelMultipleCyclicParametersFailed$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(RemoteDiagnosticsActions.cancelMultipleParametersReadCycle.failed),
        tap(() => {
          this.viToastService.error({
            message: this.translateService.instant('DIAGNOSTICS.PARAMETERS.STOP_ALL.ERROR'),
          });
        })
      );
    },
    { dispatch: false }
  );

  cancelMultipleCyclicParametersOutcome$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(RemoteDiagnosticsActions.cancelMultipleParametersReadCycle.outcome),
        tap((action) => {
          const isAnyStopCycleInError = Object.values(action.transactionOutcome).some(
            (subscriptionResult) => subscriptionResult === null
          );
          if (isAnyStopCycleInError) {
            this.viToastService.warning({
              message: this.translateService.instant('DIAGNOSTICS.PARAMETERS.STOP_ALL.WARNING'),
            });
          }
        })
      );
    },
    { dispatch: false }
  );

  scheduleMultipleParametersReadCycle$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsActions.scheduleMultipleParametersReadCycle.attempt),
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.remoteDiagnosticsStore
            .getDiagStackConnectionProperties(action.gatewaySerial)
            .pipe(take(1)),
          this.remoteDiagnosticsStore
            .getSelectedParametersStoppedAndSelectedNotInError(action.gatewaySerial)
            .pipe(take(1)),
        ])
      ),
      mergeMap(([action, diagStackConnectionProperties, parameters]) => {
        let scheduledStartCycles: DiagStackBulkScheduleReadSchedulerOutcome = {};

        parameters.forEach((parameter: RemoteDiagnosticsParameter) => {
          scheduledStartCycles[parameter.id] = this.remoteDiagnosticsCloudApiPicker
            .instance()
            .diagnosticServiceDataRead(
              diagStackConnectionProperties.node,
              diagStackConnectionProperties.xAuthToken,
              parameter.controlUnit.value,
              diagStackConnectionProperties.vciResource,
              diagStackConnectionProperties.vciName,
              parameter.vddDetail.name,
              action.interval
            )
            .pipe(
              map((response) => response),
              catchError(() => of(null))
            );
        });

        return forkJoin(scheduledStartCycles).pipe(
          map((startCyclesOutcome: DiagStackBulkScheduleReadSchedulerOutcomeResolved) => {
            const isStartCyclesFailed = Object.values(startCyclesOutcome).every(
              (subscriptionResult) => subscriptionResult === null
            );

            if (isStartCyclesFailed) {
              return RemoteDiagnosticsActions.scheduleMultipleParametersReadCycle.failed();
            } else {
              return RemoteDiagnosticsActions.scheduleMultipleParametersReadCycle.outcome({
                gatewaySerial: action.gatewaySerial,
                transactionOutcome: startCyclesOutcome,
              });
            }
          })
        );
      })
    );
  });

  scheduleMultipleParametersReadCycleFailed$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(RemoteDiagnosticsActions.scheduleMultipleParametersReadCycle.failed),
        tap(() => {
          this.viToastService.error({
            message: this.translateService.instant('DIAGNOSTICS.PARAMETERS.START_ALL.ERROR'),
          });
        })
      );
    },
    { dispatch: false }
  );

  scheduleMultipleParametersReadCycleOutcome$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(RemoteDiagnosticsActions.scheduleMultipleParametersReadCycle.outcome),
        tap((action) => {
          const isAnyStartCycleInError = Object.values(action.transactionOutcome).some(
            (subscriptionResult) => subscriptionResult === null
          );
          if (isAnyStartCycleInError) {
            this.viToastService.warning({
              message: this.translateService.instant('DIAGNOSTICS.PARAMETERS.START_ALL.WARNING'),
            });
          }
        })
      );
    },
    { dispatch: false }
  );

  logicalLinkList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsActions.logicalLinkList.attempt),
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.remoteDiagnosticsStore
            .getXAuthTokenAndDiagStackNode(action.gatewaySerial)
            .pipe(take(1)),
          this.authStore.isDemo$,
        ])
      ),
      switchMap(([action, xAuthTokenAndDiagStackNode, isDemo]) => {
        if (xAuthTokenAndDiagStackNode.xAuthToken) {
          return this.remoteDiagnosticsCloudApiPicker
            .instance()
            .diagnosticServiceLogicalLinkList(
              xAuthTokenAndDiagStackNode.node,
              xAuthTokenAndDiagStackNode.xAuthToken
            )
            .pipe(
              map((logicalLinkList) =>
                RemoteDiagnosticsActions.logicalLinkList.success({ logicalLinkList })
              ),
              concatLatestFrom(() =>
                this.remoteDiagnosticsStore
                  .getLastUserActivityTimestamp(action.gatewaySerial)
                  .pipe(take(1))
              ),
              map(([response, lastUserActivityTimestamp]) => {
                const currentUTCDate = moment.utc().subtract(30, 'minutes'); // current UTC date minus 30 minutes
                // eslint-disable-next-line
                const isExpired = currentUTCDate.isAfter(lastUserActivityTimestamp); // if user did nothing for more than 30 minutes - force disconnect
                if (isExpired && !isDemo) {
                  this.matDialog.closeAll(); // there might be some modals open, so close them first https://jira.viessmann-climatesolutions.com/browse/VIG-12099
                  this.matDialog.open(DiagnosticsConnectionForceDisconnectComponent, {
                    disableClose: true,
                    data: { gatewaySerial: action.gatewaySerial },
                  });
                }

                return response;
              }),
              catchError((error) => of(RemoteDiagnosticsActions.logicalLinkList.failed({ error })))
            );
        } else {
          return of(RemoteDiagnosticsActions.logicalLinkList.failed({ error: 'Token is missing' }));
        }
      })
    );
  });

  clearStoreOnTabChange$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        ROUTER_REQUEST,
        RemoteDiagnosticsActions.tabChanged,
        RemoteDiagnosticsActions.disconnect.attempt,
        RemoteDiagnosticsUseCasesActions.extendedParametersTabChanged,
        RemoteDiagnosticsUseCasesActions.useCaseGroupChanged,
        RemoteDiagnosticsUseCasesActions.useCaseTypeChanged
      ),
      // Check remote diagnostics connection status
      concatLatestFrom(() =>
        this.installationDetailsStore.getSelectedGateway$.pipe(
          mergeMap((gateway) =>
            this.remoteDiagnosticsStore.isActiveSessionAvailable(gateway?.serial)
          )
        )
      ),
      filter(([_action, isActiveSessionAvailable]) => !!isActiveSessionAvailable),
      map(() => {
        this.remoteDiagnosticsHelpersService.cancelRequests$.next();
        const lastTabUrl = this.location.path();
        if (lastTabUrl.includes(DiagnosticsUrlTypeEnum.ExtendedParameter)) {
          this.store$.dispatch(RemoteDiagnosticsActions.stopAndClearParamsOnTabChange());
        }

        return VddDetailsListActions.setDefaultStateOnTabChange();
      })
    );
  });

  stopAndClearParamOnTabChange$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsActions.stopAndClearParamsOnTabChange),
      tap(() => this.diagnosticsChartService.removeAllSeries()),
      map(() =>
        RemoteDiagnosticsActions.cancelMultipleParametersReadCycle.attempt({
          gatewaySerial: this.activatedRoute.snapshot.queryParams['gatewaySerial'],
          onlySelected: false,
        })
      )
    );
  });
  hideChart$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsActions.stopAndClearParamsOnTabChange),
      map(() => RemoteDiagnosticsActions.manageChartCollapsibility.hide())
    );
  });

  getConfirmPopupFlag$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsActions.getConfirmPopupFlag.attempt),
      concatLatestFrom(() => this.remoteDiagnosticsStore.isUseCasePopupBlocked$),
      filter(([_action, isUseCasePopupBlocked]) => !isUseCasePopupBlocked),
      switchMap(() =>
        this.settingsApiPicker
          .instance()
          .getKeyValueStorage(isUseCasePopupBlockedKey)
          .pipe(
            map((data) =>
              RemoteDiagnosticsActions.getConfirmPopupFlag.success({
                isUseCasePopupBlocked: data.isUseCasePopupBlocked,
              })
            ),
            catchError(() => of(RemoteDiagnosticsActions.getConfirmPopupFlag.error()))
          )
      )
    );
  });

  saveConfirmPopupFlag$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsActions.saveConfirmPopupFlag.attempt),
      switchMap(() =>
        this.settingsApiPicker
          .instance()
          .setKeyValueStorage(isUseCasePopupBlockedKey, { [isUseCasePopupBlockedKey]: true })
          .pipe(
            map(() => RemoteDiagnosticsActions.saveConfirmPopupFlag.success()),
            catchError(() => of(RemoteDiagnosticsActions.saveConfirmPopupFlag.error()))
          )
      )
    );
  });

  constructor(
    private remoteDiagnosticsCloudApi: Api.RemoteDiagnosticsCloudApi,
    private remoteDiagnosticsCloudApiPicker: Api.RemoteDiagnosticsCloudApiPicker,
    private remoteDiagnosticsMappers: RemoteDiagnosticsMappers,
    private actions$: Actions,
    private uiService: UIService,
    private viToastService: ViToastService,
    private translateService: TranslateService,
    private remoteDiagnosticsStore: RemoteDiagnosticsStore,
    private matDialog: MatDialog,
    private store$: Store,
    private authStore: AuthStore,
    private remoteDiagnosticsBackgroundService: RemoteDiagnosticsBackgroundService,
    private installationDetailsStore: InstallationDetailsStore,
    private settingsApiPicker: VitoguideBackendSettingsApiPicker,
    private diagStackDtcService: DiagStackDtcService,
    private remoteDiagnosticsHelpersService: Api.RemoteDiagnosticsHelpersService,
    private location: Location,
    private activatedRoute: ActivatedRoute,
    private diagnosticsChartService: DiagnosticsChartService,
    private featureService: FeatureService,
    private appInsights: AppInsightsMonitoringService,
    private oAuthService: OAuthService
  ) {}

  private maxPollingMs = 30000;
  private pollingIntervalMs = 5000;
  private pollingUntil$: Subject<boolean> = new Subject<boolean>();
  private endPolling$: Subject<boolean> = new Subject<boolean>();

  private timer: any;

  private createTimeout(gatewaySerial: string): void {
    this.timer = setTimeout(() => {
      this.endPolling$.next(true);
      this.store$.dispatch(
        RemoteDiagnosticsActions.remoteVciAvailabilityCheck.failed({ gatewaySerial })
      );
    }, this.maxPollingMs);
  }

  private mergeTopologyBusDetailsWithDtcErrors(
    busDetailsFromTopology: ActiveErrorWithDiagStackDetail[],
    dtcResponse: DiagStackGenericResponse
  ): ActiveErrorWithDiagStackDetail[] {
    const output = [...busDetailsFromTopology];
    const extractedDtcStructure = this.diagStackDtcService.dtcStructureExtractor(dtcResponse);

    extractedDtcStructure.forEach((structureFromParser) => {
      const structure = { ...structureFromParser };
      structure.errorCode = structureFromParser?.errorCode?.substring(3)?.replace('_', '.') || null;

      let matchingActiveError = output.find(
        (error) =>
          error.PA_BusAddre === structure.PA_BusAddre && error.PA_BusType === structure.PA_BusType
      );
      // Update existing object
      if (matchingActiveError) {
        matchingActiveError.errorCode = structure.errorCode;
        matchingActiveError.errorName = structure.errorName;
      } else {
        // Push new object to existing array
        structure.logicalLink = null;
        output.push(structure);
      }
    });

    return output;
  }
}
